import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Gift, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE_URL } from '../apiBase';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from './ui/dialog';
import OfficeModalHeader from './office/OfficeModalHeader';
import { useAuth } from '../context/AuthContext';

const API_URL = API_BASE_URL;

/** Pop-up para revendedor resgatar créditos de bonificação de migração. */
export default function MigrationBonusClaimModal() {
  const { user } = useAuth();
  const [grant, setGrant] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!user?.id) return;
    if (user.role !== 'reseller' && user.role !== 'admin') return;
    try {
      const { data } = await axios.get(`${API_URL}/api/migration-bonus/pending`, { withCredentials: true });
      const items = data?.items || [];
      setGrant(items[0] || null);
    } catch {
      /* optional */
    }
  }, [user?.id, user?.role]);

  useEffect(() => {
    fetchPending();
    const t = setInterval(fetchPending, 60_000);
    return () => clearInterval(t);
  }, [fetchPending]);

  const handleClaim = async () => {
    if (!grant?.id) return;
    setClaiming(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/migration-bonus/${grant.id}/claim`,
        {},
        { withCredentials: true },
      );
      toast.success(`${data.credits} créditos resgatados! Saldo: ${data.balance}`);
      setGrant(null);
      fetchPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao resgatar');
    } finally {
      setClaiming(false);
    }
  };

  if (!grant) return null;

  return (
    <Dialog open={!!grant} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md border-border bg-card text-foreground"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="migration-bonus-modal"
      >
        <OfficeModalHeader
          layout="row"
          variant="circle"
          icon={Gift}
          title={grant.title || 'Créditos de migração'}
          description={
            grant.message || `Você tem ${grant.credits} créditos disponíveis para resgate.`
          }
        />
        <div className="rounded-xl border border-border bg-muted/30 p-4 text-center">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Créditos</div>
          <div className="mt-1 font-display text-4xl font-semibold tabular-nums">{grant.credits}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Validade: {grant.validity_days || 90} dias após o resgate
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            className="w-full sm:w-auto"
            onClick={handleClaim}
            disabled={claiming}
            data-testid="migration-bonus-claim"
          >
            {claiming ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Gift className="h-4 w-4 mr-2" />}
            Resgatar e começar a usar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
